import {
  useMutation,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query'

import type {
  PatchWebhookConfigRequest,
  WebhookConfigDetail,
} from '@mangovoice/types'

import type { ApiError } from '../api/types'
import type { WebhooksApi } from '../api/webhooksApi'

interface UsePatchIntegrationParams {
  api: WebhooksApi
  pbxId: number
  integrationId: number
  onError?: (error: ApiError) => void
}

export const usePatchIntegration = ({
  api,
  pbxId,
  integrationId,
  onError,
}: UsePatchIntegrationParams): UseMutationResult<
  WebhookConfigDetail,
  ApiError,
  PatchWebhookConfigRequest
> => {
  const queryClient = useQueryClient()
  const queryKey = ['webhook', pbxId, integrationId]

  return useMutation<WebhookConfigDetail, ApiError, PatchWebhookConfigRequest>({
    mutationFn: async (data) => {
      const response = await api.patchWebhook(integrationId, data)

      if (!response.ok) {
        return Promise.reject(response.data as ApiError)
      }

      return response.data!
    },
    onMutate: async (data) => {
      await queryClient.cancelQueries({ queryKey })
      const snapshot = queryClient.getQueryData<WebhookConfigDetail>(queryKey)

      if (snapshot && data.active !== undefined) {
        queryClient.setQueryData<WebhookConfigDetail>(queryKey, {
          ...snapshot,
          activation: { ...snapshot.activation, is_active: data.active },
        })
      }

      return { snapshot }
    },
    onError: (_error, _data, context) => {
      const { snapshot } = context as {
        snapshot: WebhookConfigDetail | undefined
      }
      if (snapshot) {
        queryClient.setQueryData(queryKey, snapshot)
      }
      onError?.(_error)
    },
    onSettled: () => {
      queryClient.invalidateQueries({ queryKey })
      queryClient.invalidateQueries({ queryKey: ['webhooks', pbxId] })
    },
  })
}
